import { useState, useCallback, useRef, useEffect } from 'react';
import '@/App.css';
import { simulateSequence, exportToCSV } from '@/utils/calculator';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, TrendingDown, Calculator, Download, Plus, Trash2, AlertCircle, ChartCandlestick, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import BinanceChart from '@/components/BinanceChart';
import CompoundInterest from '@/components/CompoundInterest';
import Header from '@/components/Header';
import SaveSetupDialog from '@/components/SaveSetupDialog';

function App() {
  // Main page navigation
  const [mainPage, setMainPage] = useState('pattern'); // 'pattern' or 'compound'
  // Global settings
  const [initialBalance, setInitialBalance] = useState(1000);

  // Steps
  const [steps, setSteps] = useState([]);

  // Current step form (direction will be auto-determined)
  const [currentStep, setCurrentStep] = useState({
    entry: '',
    exit: '',
    leverage: 10,
    p_liq: 20 // Now in percent (0-100), default to 20% for safety
  });

  // Results
  const [results, setResults] = useState(null);
  const [originalSteps, setOriginalSteps] = useState(null);
  
  // Global p_liq for real-time adjustment
  const [globalPLiq, setGlobalPLiq] = useState(0);
  
  // Chart settings
  const [timeframe, setTimeframe] = useState('5m');
  const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'chart'
  
  // Global leverage and p_liq for chart mode
  const [globalLeverage, setGlobalLeverage] = useState(10);
  const [globalChartPLiq, setGlobalChartPLiq] = useState(20); // Default to 20% for safety
  
  // Use refs to store current values for use in callbacks
  const globalLeverageRef = useRef(globalLeverage);
  const globalChartPLiqRef = useRef(globalChartPLiq);
  
  // Update refs when state changes
  useEffect(() => {
    globalLeverageRef.current = globalLeverage;
  }, [globalLeverage]);
  
  useEffect(() => {
    globalChartPLiqRef.current = globalChartPLiq;
  }, [globalChartPLiq]);
  
  // Prices selected from chart
  const [chartPrices, setChartPrices] = useState([]);
  const lastClickTimeRef = useRef(0);
  const processingClickRef = useRef(false);

  const addStep = () => {
    if (!currentStep.entry || !currentStep.exit) {
      toast.error('Заполните цены входа и выхода');
      return;
    }

    const entry = parseFloat(currentStep.entry);
    const exit = parseFloat(currentStep.exit);
    
    // Auto-determine direction based on entry and exit prices
    const direction = entry > exit ? 'short' : 'long';

    const newStep = {
      id: Date.now(),
      direction,
      entry,
      exit,
      leverage: parseFloat(currentStep.leverage),
      p_liq: parseFloat(currentStep.p_liq),
      useMaxDeposit: true, // Always use full deposit
      capitalFraction: 1.0
    };

    setSteps([...steps, newStep]);
    toast.success(`Шаг добавлен (${direction === 'long' ? 'Long' : 'Short'})`);

    // Auto-fill next step: exit price -> entry price, keep p_liq and leverage
    setCurrentStep({
      entry: currentStep.exit, // Auto-fill with previous exit
      exit: '',
      leverage: currentStep.leverage, // Keep leverage
      p_liq: currentStep.p_liq // Keep p_liq
    });
  };

  const removeStep = (id) => {
    setSteps(steps.filter(step => step.id !== id));
    toast.info('Шаг удалён');
  };
  
  // Log state changes for debugging
  useEffect(() => {
    console.log('globalLeverage changed to:', globalLeverage);
  }, [globalLeverage]);

  useEffect(() => {
    console.log('globalChartPLiq changed to:', globalChartPLiq);
  }, [globalChartPLiq]);

  // Handle price click from chart (memoized to prevent chart re-render)
  const handleChartPriceClick = useCallback((price) => {
    // Prevent double-click from React Strict Mode
    if (processingClickRef.current) {
      console.log('Already processing a click, ignoring duplicate');
      return;
    }
    
    processingClickRef.current = true;
    
    // Reset processing flag after a short delay
    setTimeout(() => {
      processingClickRef.current = false;
    }, 100);
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // Debounce: ignore clicks within 300ms of the last click
    if (timeSinceLastClick < 300) {
      console.log('Duplicate click detected (debounced), ignoring');
      return;
    }
    
    lastClickTimeRef.current = now;
    console.log('handleChartPriceClick called with price:', price);
    
    setChartPrices(prevPrices => {
      console.log('Previous prices:', prevPrices);
      
      // Check if this exact price was just added
      if (prevPrices.length > 0 && prevPrices[prevPrices.length - 1] === price && timeSinceLastClick < 100) {
        console.log('Duplicate price just added, ignoring');
        return prevPrices;
      }
      
      const newPrices = [...prevPrices, price];
      console.log('New prices:', newPrices);
      
      // If we have at least 2 prices, create a step
      if (newPrices.length >= 2) {
        const entry = newPrices[newPrices.length - 2];
        const exit = price;
        
        // Check if this is a duplicate (same entry and exit)
        if (entry === exit) {
          console.warn('Duplicate price detected, skipping');
          return prevPrices;
        }
        
        const direction = entry > exit ? 'short' : 'long';
        
        const stepId = Date.now() + Math.random(); // More unique ID
        
        // Get current values from refs to avoid stale closure
        const currentLeverage = globalLeverageRef.current;
        const currentPLiq = globalChartPLiqRef.current;
        
        const newStep = {
          id: stepId,
          direction,
          entry,
          exit,
          leverage: currentLeverage,
          p_liq: currentPLiq,
          useMaxDeposit: true,
          capitalFraction: 1.0
        };
        
        console.log('Creating step with globalLeverage:', currentLeverage, 'globalChartPLiq:', currentPLiq);
        
        // Use a flag to ensure we only add the step once
        let stepAdded = false;
        setSteps(prevSteps => {
          // Check if a step with the same entry and exit already exists
          const isDuplicate = prevSteps.some(s => 
            s.entry === entry && s.exit === exit && (now - s.id) < 500
          );
          
          if (isDuplicate) {
            console.log('Duplicate step detected, not adding');
            return prevSteps;
          }
          
          if (!stepAdded) {
            console.log('Adding step:', newStep);
            stepAdded = true;
            
            // Only show toast once
            setTimeout(() => {
              toast.success(`Шаг добавлен (${direction === 'long' ? 'Long' : 'Short'}) ${entry.toLocaleString()} → ${exit.toLocaleString()}`);
            }, 0);
            
            return [...prevSteps, newStep];
          }
          
          return prevSteps;
        });
        
        // Keep only the last price for next entry
        return [price];
      } else {
        toast.info(`Цена входа: $${price.toLocaleString()}`);
        return newPrices;
      }
    });
  }, []); // Remove dependencies to always use current state values
  
  const clearChartPrices = () => {
    setChartPrices([]);
    setSteps([]);
    toast.info('Все точки очищены');
  };

  const calculate = () => {
    if (steps.length === 0) {
      toast.error('Добавьте хотя бы один шаг');
      return;
    }

    try {
      // Store original steps for real-time p_liq adjustment
      setOriginalSteps(steps);
      
      // Set globalPLiq based on steps' p_liq values
      // If all steps have the same p_liq, use that value
      const uniquePLiqs = [...new Set(steps.map(s => s.p_liq))];
      
      if (uniquePLiqs.length === 1) {
        // All steps have the same p_liq value
        setGlobalPLiq(uniquePLiqs[0]);
      } else if (uniquePLiqs.length > 1) {
        // Mixed p_liq values - use average
        const avgPLiq = steps.reduce((sum, s) => sum + s.p_liq, 0) / steps.length;
        setGlobalPLiq(Math.round(avgPLiq * 10) / 10); // Round to 1 decimal
      }
      // If globalPLiq was already set by user, it stays unless all steps have same value
      
      const simulationResults = simulateSequence({
        initialBalance,
        marginMode: 'isolated', // Always use isolated margin
        steps
      });

      setResults(simulationResults);
      toast.success('Расчёт выполнен успешно');
    } catch (error) {
      toast.error('Ошибка расчёта: ' + error.message);
    }
  };
  
  // Real-time recalculation when global p_liq changes
  const handleGlobalPLiqChange = (newPLiq) => {
    setGlobalPLiq(newPLiq);
    
    if (!originalSteps || originalSteps.length === 0) return;
    
    try {
      // Apply new p_liq to all steps
      const updatedSteps = originalSteps.map(step => ({
        ...step,
        p_liq: newPLiq
      }));
      
      const simulationResults = simulateSequence({
        initialBalance,
        marginMode: 'isolated',
        steps: updatedSteps
      });

      setResults(simulationResults);
    } catch (error) {
      console.error('Error during real-time recalculation:', error);
    }
  };

  const downloadCSV = () => {
    if (!results) {
      toast.error('Сначала выполните расчёт');
      return;
    }

    const csv = exportToCSV(results);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `futures_calculator_${Date.now()}.csv`;
    link.click();
    toast.success('CSV файл загружен');
  };

  const chartData = results?.steps.filter(s => !s.error).map((step, idx) => {
    if (results.marginMode === 'cross') {
      // For cross margin, calculate cumulative balance
      const cumulativePnL = results.steps
        .slice(0, idx + 1)
        .filter(s => !s.error)
        .reduce((sum, s) => sum + s.PnL_USD, 0);
      return {
        step: step.stepIndex,
        balance: results.initialBalance + cumulativePnL
      };
    }
    return {
      step: step.stepIndex,
      balance: step.balance_after
    };
  }) || [];

  return (
    <div className="App">
      <Toaster position="top-right" />
      <Header />
      
      <div className="min-h-screen bg-[#0a0e1a] pt-16">
        {/* Sub-Header Navigation */}
        <div className="border-b border-slate-800/50 bg-[#0f1419]">
          <div className="container mx-auto px-4 py-4 md:py-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">              
              {/* Main Navigation */}
              <div className="flex gap-2 w-full md:w-auto">
                <Button
                  onClick={() => setMainPage('pattern')}
                  variant={mainPage === 'pattern' ? 'default' : 'outline'}
                  className={`flex-1 md:flex-none ${mainPage === 'pattern' 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 border-0' 
                    : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
                  }`}
                >
                  <ChartCandlestick className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Паттерн</span>
                </Button>
                <Button
                  onClick={() => setMainPage('compound')}
                  variant={mainPage === 'compound' ? 'default' : 'outline'}
                  className={`flex-1 md:flex-none ${mainPage === 'compound' 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20 border-0' 
                    : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
                  }`}
                >
                  <Percent className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Сложный процент</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Compound Interest Page */}
          {mainPage === 'compound' && (
            <CompoundInterest />
          )}

          {/* Pattern Trading Page */}
          {mainPage === 'pattern' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Left Column - Settings */}
            <div className="lg:col-span-1 space-y-6">
              {/* Global Settings */}
              <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur" data-testid="global-settings-card">
                <CardHeader>
                  <CardTitle className="text-white">Глобальные настройки</CardTitle>
                  <CardDescription className="text-slate-400">Начальные параметры симуляции</CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="initialBalance" className="text-slate-300">Начальный баланс (USD)</Label>
                    <Input
                      id="initialBalance"
                      data-testid="initial-balance-input"
                      type="number"
                      value={initialBalance}
                      onChange={(e) => setInitialBalance(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      onBlur={(e) => setInitialBalance(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                      className="bg-slate-900/80 border-slate-600 text-white mt-2"
                      placeholder="1000"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Tabs for Manual vs Chart */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-slate-800/50 h-auto">
                  <TabsTrigger value="manual" className="data-[state=active]:bg-blue-600 py-3 text-sm md:text-base">
                    Ручной ввод
                  </TabsTrigger>
                  <TabsTrigger value="chart" className="data-[state=active]:bg-blue-600 py-3 text-sm md:text-base">
                    <ChartCandlestick className="w-4 h-4 mr-1 md:mr-2" />
                    График
                  </TabsTrigger>
                </TabsList>

                {/* Manual Input Tab */}
                <TabsContent value="manual" className="mt-4">
                  <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur" data-testid="add-step-card">
                <CardHeader>
                  <CardTitle className="text-white">Добавить шаг</CardTitle>
                  <CardDescription className="text-slate-400">Направление определяется автоматически</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="entry" className="text-slate-300">Цена входа</Label>
                      <Input
                        id="entry"
                        data-testid="entry-input"
                        type="number"
                        value={currentStep.entry}
                        onChange={(e) => setCurrentStep({ ...currentStep, entry: e.target.value })}
                        className="bg-slate-900/80 border-slate-600 text-white mt-2"
                        placeholder="50000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="exit" className="text-slate-300">Цена выхода</Label>
                      <Input
                        id="exit"
                        data-testid="exit-input"
                        type="number"
                        value={currentStep.exit}
                        onChange={(e) => setCurrentStep({ ...currentStep, exit: e.target.value })}
                        className="bg-slate-900/80 border-slate-600 text-white mt-2"
                        placeholder="51000"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="leverage" className="text-slate-300">Плечо (L)</Label>
                      <Input
                        id="leverage"
                        data-testid="leverage-input"
                        type="number"
                        value={currentStep.leverage}
                        onChange={(e) => setCurrentStep({ ...currentStep, leverage: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        onBlur={(e) => setCurrentStep({ ...currentStep, leverage: e.target.value === '' ? 1 : parseFloat(e.target.value) })}
                        className="bg-slate-900/80 border-slate-600 text-white mt-2"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="p_liq" className="text-slate-300">% до ликвидации (опц.)</Label>
                      <Input
                        id="p_liq"
                        data-testid="p-liq-input"
                        type="number"
                        step="1"
                        min="0"
                        max="99"
                        value={currentStep.p_liq}
                        onChange={(e) => setCurrentStep({ ...currentStep, p_liq: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        onBlur={(e) => setCurrentStep({ ...currentStep, p_liq: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                        className="bg-slate-900/80 border-slate-600 text-white mt-2"
                        placeholder="0"
                      />
                      <p className="text-xs text-slate-500 mt-1">Например: 20 = 20% до ликвидации</p>
                    </div>
                  </div>
                  <Button onClick={addStep} className="w-full bg-blue-600 hover:bg-blue-700 py-3 md:py-2 text-base" data-testid="add-step-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить шаг
                  </Button>
                </CardContent>
              </Card>
                </TabsContent>

                {/* Chart Input Tab */}
                <TabsContent value="chart" className="mt-4">
                  <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur">
                    <CardHeader>
                      <CardTitle className="text-white">Настройки для графика</CardTitle>
                      <CardDescription className="text-slate-400">Применяются ко всем точкам</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="globalLeverage" className="text-slate-300">Плечо (L)</Label>
                          <Input
                            id="globalLeverage"
                            type="number"
                            value={globalLeverage}
                            onChange={(e) => {
                              const newValue = e.target.value === '' ? '' : parseFloat(e.target.value);
                              console.log('globalLeverage onChange:', e.target.value, '-> parsed:', newValue);
                              setGlobalLeverage(newValue);
                            }}
                            onBlur={(e) => {
                              const newValue = e.target.value === '' ? 1 : parseFloat(e.target.value);
                              console.log('globalLeverage onBlur:', e.target.value, '-> parsed:', newValue);
                              setGlobalLeverage(newValue);
                            }}
                            className="bg-slate-900/80 border-slate-600 text-white mt-2"
                            placeholder="10"
                          />
                        </div>
                        <div>
                          <Label htmlFor="globalChartPLiq" className="text-slate-300">% до ликвидации</Label>
                          <Input
                            id="globalChartPLiq"
                            type="number"
                            step="1"
                            min="0"
                            max="99"
                            value={globalChartPLiq}
                            onChange={(e) => {
                              const newValue = e.target.value === '' ? '' : parseFloat(e.target.value);
                              console.log('globalChartPLiq onChange:', e.target.value, '-> parsed:', newValue);
                              setGlobalChartPLiq(newValue);
                            }}
                            onBlur={(e) => {
                              const newValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              console.log('globalChartPLiq onBlur:', e.target.value, '-> parsed:', newValue);
                              setGlobalChartPLiq(newValue);
                            }}
                            className="bg-slate-900/80 border-slate-600 text-white mt-2"
                            placeholder="20"
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-blue-950/30 border border-emerald-700/50 rounded-lg">
                        <p className="text-sm text-blue-300">
                          💡 Кликайте на график ниже, чтобы выбрать цены входа и выхода
                        </p>
                        <p className="text-xs text-slate-400 mt-2">
                          {chartPrices.length === 0 ? 'Следующий клик: Цена входа' : `Последняя цена: $${chartPrices[chartPrices.length - 1].toLocaleString()}`}
                        </p>
                      </div>
                      {chartPrices.length > 0 && (
                        <Button onClick={clearChartPrices} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Очистить все точки ({chartPrices.length})
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Steps List - Hide on mobile when chart tab is active (will show in right column) */}
              {steps.length > 0 && (
                <Card className={`bg-slate-900/60 border-slate-800/50 backdrop-blur ${activeTab === 'chart' ? 'hidden lg:block' : ''}`} data-testid="steps-list-card">
                  <CardHeader>
                    <CardTitle className="text-white">Добавленные шаги ({steps.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {steps.map((step, index) => (
                      <div key={step.id} className="flex items-center justify-between p-2 md:p-3 bg-slate-900/80 rounded-lg border border-slate-700" data-testid={`step-item-${index}`}>
                        <div className="flex items-center gap-2 md:gap-3">
                          {step.direction === 'long' ? (
                            <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
                          ) : (
                            <TrendingDown className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
                          )}
                          <div>
                            <div className="text-white text-xs md:text-sm font-medium">
                              {step.direction.toUpperCase()}: {step.entry} → {step.exit}
                            </div>
                            <div className="text-slate-400 text-xs">
                              L={step.leverage}x
                            </div>
                          </div>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeStep(step.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-950/30"
                          data-testid={`remove-step-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={calculate}
                  disabled={steps.length === 0}
                  className="w-full bg-gradient-to-r bg-blue-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-4 md:py-6 shadow-lg text-base md:text-lg"
                  data-testid="calculate-button"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Рассчитать
                </Button>
                {results && (
                  <>
                    <SaveSetupDialog 
                      results={results} 
                      initialBalance={initialBalance}
                      onSaved={(savedSetup) => {
                        console.log('Setup saved:', savedSetup);
                      }}
                    />
                    <Button
                      onClick={downloadCSV}
                      variant="outline"
                      className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 py-3 md:py-2"
                      data-testid="download-csv-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Экспорт в CSV
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Binance Chart - Always visible in chart mode */}
              {activeTab === 'chart' && (
                <>
                  <BinanceChart 
                    onPriceClick={handleChartPriceClick}
                    timeframe={timeframe}
                    setTimeframe={setTimeframe}
                  />
                  
                  {/* Steps List - Show below chart on mobile only */}
                  {steps.length > 0 && (
                    <Card className="lg:hidden bg-slate-900/60 border-slate-800/50 backdrop-blur" data-testid="steps-list-card-mobile">
                      <CardHeader>
                        <CardTitle className="text-white">Добавленные шаги ({steps.length})</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {steps.map((step, index) => (
                          <div key={step.id} className="flex items-center justify-between p-2 md:p-3 bg-slate-900/80 rounded-lg border border-slate-700" data-testid={`step-item-mobile-${index}`}>
                            <div className="flex items-center gap-2 md:gap-3">
                              {step.direction === 'long' ? (
                                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
                              ) : (
                                <TrendingDown className="w-4 h-4 md:w-5 md:h-5 text-red-500" />
                              )}
                              <div>
                                <div className="text-white text-xs md:text-sm font-medium">
                                  {step.direction.toUpperCase()}: {step.entry} → {step.exit}
                                </div>
                                <div className="text-slate-400 text-xs">
                                  L={step.leverage}x
                                </div>
                              </div>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => removeStep(step.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-950/30"
                              data-testid={`remove-step-mobile-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              
              {results ? (
                <>
                  {/* Global P_liq Control */}
                  <Card className="bg-gradient-to-br from-emerald-900/20 to-slate-800 border-emerald-700/50 backdrop-blur" data-testid="global-pliq-card">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-blue-300 font-semibold text-base">
                          % до ликвидации (для всех сделок)
                        </Label>
                        <span className="text-2xl font-bold text-blue-400">{globalPLiq}%</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="50"
                        step="0.1"
                        value={globalPLiq}
                        onChange={(e) => handleGlobalPLiqChange(parseFloat(e.target.value))}
                        className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                        data-testid="global-pliq-slider"
                      />
                      <div className="flex justify-between text-xs text-slate-400 mt-2">
                        <span>0% (макс риск)</span>
                        <span>50% (мин риск)</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-3 text-center">
                        Измените процент для пересчёта всех позиций в реальном времени
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700" data-testid="initial-balance-card">
                      <CardHeader className="pb-3">
                        <CardDescription className="text-slate-400">Начальный баланс</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-white">${results.initialBalance.toLocaleString()}</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700" data-testid="final-balance-card">
                      <CardHeader className="pb-3">
                        <CardDescription className="text-slate-400">Итоговый баланс</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-white">${results.finalBalance.toLocaleString()}</div>
                      </CardContent>
                    </Card>
                    <Card className={`bg-gradient-to-br border-slate-700 ${
                      results.totalROI >= 0 ? 'from-emerald-900/30 to-slate-900' : 'from-red-900/30 to-slate-900'
                    }`} data-testid="total-roi-card">
                      <CardHeader className="pb-3">
                        <CardDescription className="text-slate-400">ROI</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${
                          results.totalROI >= 0 ? 'text-blue-400' : 'text-red-400'
                        }`}>
                          {results.totalROI >= 0 ? '+' : ''}{results.totalROI}%
                        </div>
                        <div className={`text-sm mt-1 ${
                          results.absoluteProfit >= 0 ? 'text-blue-300' : 'text-red-300'
                        }`}>
                          {results.absoluteProfit >= 0 ? '+' : ''}${results.absoluteProfit.toLocaleString()}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Chart */}
                  {chartData.length > 0 && (
                    <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur" data-testid="balance-chart-card">
                      <CardHeader>
                        <CardTitle className="text-white">График баланса</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={250} className="md:h-[300px]">
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                            <XAxis dataKey="step" stroke="#94a3b8" label={{ value: 'Шаг', position: 'insideBottom', offset: -5, fill: '#94a3b8' }} />
                            <YAxis stroke="#94a3b8" label={{ value: 'Баланс (USD)', angle: -90, position: 'insideLeft', fill: '#94a3b8' }} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                              labelStyle={{ color: '#f1f5f9' }}
                              itemStyle={{ color: '#10b981' }}
                            />
                            <Legend wrapperStyle={{ color: '#94a3b8' }} />
                            <Line type="monotone" dataKey="balance" stroke="#10b981" strokeWidth={3} name="Баланс" dot={{ fill: '#10b981', r: 5 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Results Table */}
                  <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur" data-testid="results-table-card">
                    <CardHeader>
                      <CardTitle className="text-white">Детальные результаты</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
                        <table className="w-full text-xs md:text-sm min-w-[800px]">
                          <thead>
                            <tr className="border-b border-slate-700">
                              <th className="text-left py-3 px-4 text-slate-300 font-semibold">Шаг</th>
                              <th className="text-left py-3 px-4 text-slate-300 font-semibold">Направление</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">Вход</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">Выход</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">Плечо</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">Ликвидация</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">Макс BTC</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">PnL</th>
                              <th className="text-right py-3 px-4 text-slate-300 font-semibold">Баланс</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.steps.map((step, idx) => {
                              // Calculate cumulative balance for cross margin
                              let displayBalance = step.balance_after;
                              if (results.marginMode === 'cross' && !step.error) {
                                const cumulativePnL = results.steps
                                  .slice(0, idx + 1)
                                  .filter(s => !s.error)
                                  .reduce((sum, s) => sum + s.PnL_USD, 0);
                                displayBalance = results.initialBalance + cumulativePnL;
                              }
                              
                              return (
                                <tr key={step.stepIndex} className="border-b border-slate-700/50 hover:bg-slate-900/30" data-testid={`result-row-${step.stepIndex}`}>
                                  {step.error ? (
                                    <td colSpan="7" className="py-3 px-4 text-red-400">
                                      <div className="flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        {step.error}
                                      </div>
                                    </td>
                                  ) : (
                                    <>
                                      <td className="py-3 px-4 text-slate-300">{step.stepIndex}</td>
                                      <td className="py-3 px-4">
                                        <div className="flex items-center gap-2">
                                          {step.direction === 'long' ? (
                                            <TrendingUp className="w-4 h-4 text-blue-500" />
                                          ) : (
                                            <TrendingDown className="w-4 h-4 text-red-500" />
                                          )}
                                          <span className="text-slate-300">{step.direction}</span>
                                        </div>
                                      </td>
                                      <td className="py-3 px-4 text-right text-slate-300">${step.entry.toLocaleString()}</td>
                                      <td className="py-3 px-4 text-right text-slate-300">${step.exit.toLocaleString()}</td>
                                      <td className="py-3 px-4 text-right text-slate-300">{step.L_eff}x</td>
                                      <td className="py-3 px-4 text-right text-orange-400">${step.liquidation_price?.toLocaleString() || 'N/A'}</td>
                                      <td className="py-3 px-4 text-right text-cyan-400">{step.max_btc_amount?.toFixed(6) || 'N/A'} BTC</td>
                                      <td className={`py-3 px-4 text-right font-semibold ${
                                        step.PnL_USD >= 0 ? 'text-blue-400' : 'text-red-400'
                                      }`}>
                                        {step.PnL_USD >= 0 ? '+' : ''}${step.PnL_USD.toLocaleString()}
                                      </td>
                                      <td className="py-3 px-4 text-right text-white font-semibold">${displayBalance.toLocaleString()}</td>
                                    </>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Warnings */}
                      {results.steps.some(s => s.warnings?.length > 0) && (
                        <div className="mt-6 space-y-2">
                          <h4 className="text-yellow-400 font-semibold flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Предупреждения
                          </h4>
                          {results.steps.map((step) => (
                            step.warnings?.map((warning, idx) => (
                              <div key={`${step.stepIndex}-${idx}`} className="text-sm text-slate-300 pl-6">
                                Шаг {step.stepIndex}: {warning}
                              </div>
                            ))
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur" data-testid="empty-state-card">
                  <CardContent className="py-20">
                    <div className="text-center text-slate-400">
                      <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <h3 className="text-xl font-semibold mb-2">Готов к расчёту</h3>
                      <p className="text-sm">Добавьте шаги и нажмите "Рассчитать" для получения результатов</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;